import { useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';

import { getCertificateDownload } from '@/entities/user';

interface DownloadOptions {
  fileName?: string;
  shareTitle?: string;
}

const DEFAULT_FILE_NAME = 'certificate.pdf';
const DEFAULT_SHARE_TITLE = 'My Real Estate Course Certificate';

export const useDownloadCertificate = () => {
  const { mutateAsync: requestCertificate, isPending } = useMutation({
    mutationFn: getCertificateDownload,
  });

  const download = useCallback(
    async (certificateId: string, options?: DownloadOptions) => {
      if (!certificateId || typeof window === 'undefined') return;

      const { navigator } = window;

      const fileName = options?.fileName ?? DEFAULT_FILE_NAME;
      const shareTitle = options?.shareTitle ?? DEFAULT_SHARE_TITLE;

      try {
        const blob = await requestCertificate(certificateId);
        const file = new File([blob], fileName, { type: blob.type });

        const isMobileBrowser = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent,
        );
        const canUseShare =
          typeof navigator.canShare === 'function' &&
          navigator.canShare({ files: [file] }) &&
          typeof navigator.share === 'function';

        if (isMobileBrowser && canUseShare) {
          await navigator.share({
            title: shareTitle,
            files: [file],
          });
          return;
        }

        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = fileName;
        anchor.rel = 'noopener noreferrer';
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        URL.revokeObjectURL(url);
      } catch (error) {
        // Surface the original error to allow caller-specific handling.
        throw error;
      }
    },
    [requestCertificate],
  );

  return { download, isPending };
};
